'use client'

import { DateTime } from "luxon";
import Link from "next/link";
import styled from "styled-components";
import TopBarMinimal from "@/components/TopBar/minimal";
import { SOCIAL } from "@/utils/variables";

import BlogBackground from "@/assets/img/blog-background.png"
import { smallDesktop } from "@/assets/styles/themeConfig";
import Post from "@/components/Post";
import { PostType } from "@/components/Post/Post";

const HeaderBlue = styled.div<{background: { src: string; }}>`
  background: url(${BlogBackground.src});
  padding-bottom: 3rem;
`

const HeaderBlock = styled.div`
  width: 100%;
  max-width: 75rem;
  margin: 6rem auto 8rem;
  padding: 0 1rem;
  text-align: center;

  @media ${({ theme }) => theme.mediaQuery.smallPhone} {
    margin: 6rem auto 8rem;
  }

  h1.hero {
    /* text-transform: uppercase; */
    font-size: 3.5rem;
    letter-spacing: -0.2rem;
    line-height: 1;
    font-weight: 600;
    font-family: ${({ theme }) => theme.fonts.secondary};
    color: white;

    @media ${({ theme }) => theme.mediaQuery.phone} {
      font-size: 5rem;
      letter-spacing: -0.2rem;
    }

    @media ${({ theme }) => theme.mediaQuery.smallTablet} {
      font-size: 8rem;
      letter-spacing: -0.4rem;
    }
  }

  h4 {
    font-family: ${({ theme }) => theme.fonts.secondary};
    color: white;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: 1.75rem 1rem;
  width: 100%;
  max-width: 75rem;
  margin: 4rem auto;
  padding: 0 1rem;

  &.highlights {
    margin-bottom: 1.75rem;

    & + div {
      margin-top: 1.75rem;
    }
  }

  @media ${({ theme }) => theme.mediaQuery.phone} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${({ theme }) => theme.mediaQuery.smallTablet} {
    grid-template-columns: repeat(3, 1fr);

    &.highlights {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media ${({ theme }) => theme.mediaQuery.largeDesktop} {
    grid-template-columns: repeat(4, 1fr);
    max-width: 90rem;

    &.highlights {
      grid-template-columns: repeat(3, 1fr);
    }
  }
`

const HeroPost = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: 1.75rem 1rem;
  width: 100%;
  max-width: 75rem;
  margin: -5rem auto 0;
  padding: 0 1rem;

  @media ${({ theme }) => theme.mediaQuery.largeDesktop} {
    max-width: 90rem;
  }

  .image-container.image-container {
    @media ${({ theme }) => theme.mediaQuery.smallTablet} {
      aspect-ratio: 16/9;
      padding-top: 0;
      height: auto;
    }
  }

  h2 {
    font-size: 2rem;
    line-height: 1.3;
    max-width: ${smallDesktop}px;
  }

  .description {
    max-width: ${smallDesktop}px;
  }
`

const SocialContainer = styled.div`
  padding: 0rem 1rem 4rem;
  text-align: center;

  h4 {
    font-family: ${({ theme }) => theme.fonts.secondary};
  }

  @media ${({ theme }) => theme.mediaQuery.phone} {
    padding: 0rem 1rem 4rem;
  }
  
  .social {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1rem;
    
    a {
      padding: 0.2rem;
      margin: 0 0.2rem;
      font-size: 1.6rem;
      color: black;
      opacity: 0.4;

      &:hover {
        opacity: 1;
      }
    }
  }
`

const DateSortedAndFilteredArr = (arr: PostType[]): PostType[] => {

  // if development show the posts that are in the future
  if(process.env.NODE_ENV === "production") {
    return arr.filter((post: PostType) => {
      const postDate = DateTime.fromISO(post.frontmatter.date, { zone: 'America/New_York' });
      const now = DateTime.now().setZone('America/New_York');
      
      return postDate < now;
    });
  }

  return arr;
}

const Page = ({ posts }: { posts: PostType[] }) => {

  const [firstPost, ...allPosts] = DateSortedAndFilteredArr(posts);
  const highlightPosts = allPosts.slice(0, 6);
  const restPosts = allPosts.slice(6);
  
  return (
    <main>
      <HeaderBlue background={BlogBackground}>
        <TopBarMinimal mode="dark" noblur noscrolling />
        <HeaderBlock>
          <h1 className="hero">
            Intersect
          </h1>
          {/* <h4>Where design trends, productivity motivation &amp; minimal products come together</h4> */}
          {/*<h4>All things minimal products, productivity motivation &amp; design trends</h4>*/}
          <h4>All things product, productivity and design trends</h4>
        </HeaderBlock>
      </HeaderBlue>
      <HeroPost>
        <Post post={firstPost} hasDescription />
      </HeroPost>
      <Grid className="highlights">
        {highlightPosts.map((post) => {
          //extract slug and frontmatter
          const { frontmatter } = post
          //extract frontmatter properties
          const { title } = frontmatter

          //JSX for individual blog listing
          return (
            <div className="grid-item" key={title}>
              <Post post={post} />
            </div>
          )
        })}
      </Grid>
      <Grid>
        {restPosts.map((post) => {
          //extract slug and frontmatter
          const { frontmatter } = post
          //extract frontmatter properties
          const { title } = frontmatter

          //JSX for individual blog listing
          return (
            <div className="grid-item" key={title}>
              <Post post={post} />
            </div>
          )
        })}
      </Grid>
      <SocialContainer>
        <h4>Want more? Follow us!</h4>
        <div className="social">
          {SOCIAL.map(({url, icon}, index) => (
            <Link key={index} href={url}>
              <i aria-hidden className={`fab ${icon}`} />
            </Link>
          ))}
        </div>
      </SocialContainer>
    </main>
  )
}

export default Page;